// ===============================================================
// 🔥 UserProfilePage.js — صفحة بروفايل الزائر (LIVORA FINAL)
// - VPN PLAN: visitor sees displayCountry (privacy) not real profile country
// - LIVORA PLAN: NO dir logic here (html dir only)
// - "الإشعارات والمتابعة": Follow/Unfollow via Cloud Functions only
// - "خطة البطاقات": no browser alert/confirm, use in-app toast
// - "خطة الصورة": robust image resolving + fallback
// - UPDATE: better element order + age + glassy icon wrappers
// - UPDATE: received gifts grouped by type with correct gift catalog binding
// path: src/components/UserProfilePage.js
// ===============================================================

import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
  useCallback,
} from "react";
import "./UserProfilePage.css";
import {
  canCallUser as canCallUserPolicy,
  isUserBusy,
  resolveCallTapAction,
} from "../utils/callAvailability";
import goldCoin from "../assets/gold.png";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  limit,
} from "firebase/firestore";
import { FaVideo, FaRegEnvelope, FaBirthdayCake } from "react-icons/fa";
import { auth, db, functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import { useTranslation } from "react-i18next";
import { getGiftById, GIFTS } from "../gifts/giftsCatalog";
import { defaultAvatarByGender } from "../utils/defaultAvatar";
import {
  getProfilePhotoRawFromUser,
  resolveProfilePhotoUrlCore,
} from "../utils/profilePhotoResolver";
import { CountryFlagGlyph } from "../utils/countryFlags";
import { calculateAgeFromBirthDateString } from "../utils/localizedDate";
import { formatNumberEn } from "../utils/digits";
import { buildExistingChatRoute } from "../navigation/nativeChatRouteBuilder";
import { useCallStartOverlay } from "../calls/useCallStartOverlay";
import { getStartCallErrorToast } from "../calls/startCallErrors";
import {
  startCallStartupTraceAt,
  logCallStartupEvent,
} from "../calls/callStartupTrace";
import {
  buildReturnContextFromLocation,
  saveReturnContext,
} from "../calls/nativeCallReturnContext";
// ===============================================================
// 🌍 Country Helpers (FINAL — robust + VPN privacy)
// ===============================================================

function normalizeIso2(v) {
  const raw = (v || "").toString().trim().toUpperCase();
  if (!raw) return "";

  const first = raw.split(/[\s,|/\\-]+/)[0] || "";
  if (/^[A-Z]{2}$/.test(first)) return first;

  const m = raw.match(/[A-Z]{2}/);
  return m ? m[0] : "";
}

function resolvePrivacyCountry(user) {
  if (!user) return { code: "", legacy: "" };

  const code =
    normalizeIso2(user.displayCountry) ||
    normalizeIso2(user?.premium?.country) ||
    normalizeIso2(user?.vpn?.country) ||
    normalizeIso2(user?.settings?.vpnVisibleCountry) ||
    normalizeIso2(user?.countryKey) ||
    normalizeIso2(user?.country);

  const legacy = (user.countryName || user.country || user.countryKey || "")
    .toString()
    .trim();

  return { code, legacy };
}

function profileReceiveMode(u) {
  const r = String(
    u?.receiveMode ?? u?.settings?.messages?.receiveMode ?? "both"
  )
    .trim()
    .toLowerCase();
  if (r === "messages" || r === "off") return r;
  return "both";
}

function profileShowGifts(u) {
  if (u?.showGifts === false) return false;
  if (u?.settings?.messages?.showGifts === false) return false;
  return true;
}

function buildCountryUi(user, t) {
  const { code, legacy } = resolvePrivacyCountry(user);

  if (code) {
    const key = code.toLowerCase();
    return {
      code,
      text: t(`countries.${key}`, { defaultValue: code }),
    };
  }

  return {
    code: "",
    text: legacy || t("unknown_country", { defaultValue: "غير معروفة" }),
  };
}

// ===============================================================
// 🎂 Age Helpers
// ===============================================================
// calculateAgeFromBirthDateString (src/utils/localizedDate.js) is the
// single shared, timezone-safe implementation - it never constructs
// `new Date(dateValue)` from the raw string, so it can't be shifted by
// a day (or a month, at a leap-day/month-end boundary) depending on the
// viewer's timezone the way the previous local calculateAgeFromDate/
// parsePossibleDate implementation here was proven to be. The canonical
// field is `birthDate` (a plain "YYYY-MM-DD" string, written once by
// functions/auth/completeOnboarding.js).
//
// This deliberately does NOT read any numeric age field first
// (previously user?.age / user?.userAge / user?.profile?.age, and before
// that also dateOfBirth/dob/birthday aliases) - a repo-wide search
// (production source, functions/, and every script/migration/fixture
// path) found zero writers for any of age/maleAge/femaleAge/userAge/
// profile.age anywhere in this codebase, so there is no historical data
// this app itself could have produced that a birthDate-first policy
// would regress. A stored numeric age is inherently stale the moment it
// exists (it doesn't advance on the person's next birthday the way a
// birthDate-derived value does), so even if such a field is ever found
// in production data from outside this codebase, it must never take
// precedence over a valid canonical birthDate - only birthDate is
// authoritative.
// Exported (only) so its birthDate-exclusive precedence over any
// numeric age-like field can be unit-tested directly, without rendering
// the full component - see UserProfilePage.getUserAge.test.js.
export function getUserAge(user) {
  return calculateAgeFromBirthDateString(user?.birthDate);
}

// ===============================================================
// 💰 Female Call Prices (UI mirror of server FINAL)
// ===============================================================
const FEMALE_CALL_PRICES = {
  0: 20,
  1: 20,
  2: 25,
  3: 30,
  4: 35,
  5: 40,
  6: 45,
  7: 50,
  8: 60,
  9: 70,
  10: 80,
};

function clampLevel(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return 0;
  return Math.max(0, Math.min(10, x));
}

function getFemalePriceLevel(u) {
  return clampLevel(u?.callPriceLevel ?? u?.level ?? 0);
}

function getFemalePricePerMinute(u) {
  const lvl = getFemalePriceLevel(u);
  return FEMALE_CALL_PRICES[lvl] ?? FEMALE_CALL_PRICES[0];
}

function isFemaleUser(u) {
  const g = (u?.gender || "").toString().trim().toLowerCase();
  return g === "female" || typeof u?.callPriceLevel === "number";
}

// ===============================================================
// 🎁 Gifts helpers
// ===============================================================
function normalizeGiftKey(v) {
  return (v || "").toString().trim().toLowerCase();
}

function normalizeGiftMatchKey(v) {
  return normalizeGiftKey(v).replace(/[\s_-]+/g, "");
}

function getGiftCatalogItem(gift) {
  const giftId = normalizeGiftKey(gift?.giftId);
  if (giftId) {
    const byId = getGiftById(giftId);
    if (byId) return byId;
  }

  const nameKey = normalizeGiftMatchKey(gift?.giftName);
  if (!nameKey) return null;

  return (
    GIFTS.find((item) => normalizeGiftMatchKey(item?.name) === nameKey) || null
  );
}

function ReceivedGiftVisual({ giftPreview, giftIcon, giftName }) {
  const [imgFailed, setImgFailed] = useState(false);
  const preview = String(giftPreview || "").trim();
  const icon = String(giftIcon || "🎁").trim();

  if (!preview || imgFailed) {
    return <span>{icon}</span>;
  }

  return (
    <img
      src={preview}
      alt={giftName}
      className="la-gift-photo"
      loading="lazy"
      onError={() => setImgFailed(true)}
    />
  );
}

function getGiftPreview(gift) {
  const item = getGiftCatalogItem(gift);
  return String(item?.previewUrl || "").trim();
}

function getGiftIcon(gift) {
  const item = getGiftCatalogItem(gift);
  return String(item?.icon || gift?.icon || "🎁").trim();
}

function getGiftDisplayName(gift, t) {
  const item = getGiftCatalogItem(gift);
  const catalogName = String(item?.name || "").trim();
  if (catalogName) return catalogName;

  const raw = String(gift?.giftName || "").trim();
  if (raw) return raw;

  return t("gift", { defaultValue: "Gift" });
}

function getGiftGroupKey(gift, t) {
  const idPart = normalizeGiftKey(gift?.giftId);
  const namePart = normalizeGiftKey(getGiftDisplayName(gift, t));
  return idPart || namePart || "gift";
}

function getChatId(uid1, uid2) {
  if (!uid1 || !uid2) return null;
  return uid1 < uid2 ? `${uid1}_${uid2}` : `${uid2}_${uid1}`;
}

function UserProfilePage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // ======================== متابعة ========================
  const [isFollowing, setIsFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);

  // ======================== Gifts ========================
  const [receivedGifts, setReceivedGifts] = useState([]);

  // ======================== Toast ========================
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const showToast = useCallback((message, type = "info") => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ message, type });
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
    }, 2600);
  }, []);

  const { begin: beginCallStart, logReturned, logFailed, starting, overlay } =
    useCallStartOverlay();

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  // =========================================================
  // ✅ Country UI (VPN privacy)
  // =========================================================
  const countryUI = useMemo(() => buildCountryUi(user, t), [user, t]);
  const userAge = useMemo(() => getUserAge(user), [user]);

  // =========================================================
  // ✅ تجميع الهدايا المتكررة مع الربط الصحيح من giftsCatalog
  // =========================================================
  const groupedReceivedGifts = useMemo(() => {
    const map = new Map();

    for (const gift of receivedGifts) {
      const key = getGiftGroupKey(gift, t);

      if (!map.has(key)) {
        map.set(key, {
          key,
          giftId: gift?.giftId || "",
          giftName: getGiftDisplayName(gift, t),
          giftIcon: getGiftIcon(gift),
          giftPreview: getGiftPreview(gift),
          count: 1,
        });
      } else {
        const current = map.get(key);
        current.count += 1;
      }
    }

    return Array.from(map.values()).sort((a, b) => b.count - a.count);
  }, [receivedGifts, t]);

  const totalReceivedGiftCount = useMemo(() => {
    const fromUserStats = Number(
      user?.giftsStats?.receivedCount ??
        user?.stats?.receivedGiftsCount ??
        user?.stats?.giftsReceivedCount ??
        0
    );
    if (Number.isFinite(fromUserStats) && fromUserStats > 0) {
      return fromUserStats;
    }
    return receivedGifts.length;
  }, [user, receivedGifts]);

  // =========================================================
  // زر الرسالة — فتح الشات فقط
  // =========================================================
  const handleMessageClick = () => {
    const currentUser = auth.currentUser;
    if (!currentUser || !id) {
      showToast(
        t("login_required", { defaultValue: "يرجى تسجيل الدخول أولاً" }),
        "error"
      );
      return;
    }

    if (currentUser.uid === id) return;

    if (profileReceiveMode(user) === "off") return;

    const chatId = getChatId(currentUser.uid, id);
    const chatRoute = chatId ? buildExistingChatRoute(chatId) : null;
    if (!chatRoute) {
      showToast(
        t("chat_open_failed", { defaultValue: "تعذر فتح المحادثة" }),
        "error"
      );
      return;
    }

    navigate(chatRoute);
  };

  // =========================================================
  // ⭐ زر الاتصال بالفيديو — startCall + فحص الحظر
  // =========================================================
  const handleStartCall = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser || !id) {
      showToast(
        t("login_required", { defaultValue: "يرجى تسجيل الدخول أولاً" }),
        "error"
      );
      return;
    }

    if (currentUser.uid === id) {
      showToast(
        t("call_self_error", {
          defaultValue: "لا يمكنك الاتصال بنفسك 😅",
        }),
        "error"
      );
      return;
    }

    if (profileReceiveMode(user) !== "both") {
      return;
    }

    if (!beginCallStart()) return;

    const callButtonTapAt = performance.now();

    try {
      const isBlockedFn = httpsCallable(functions, "isBlocked");
      const resBlock = await isBlockedFn({ otherUid: id });
      const { blocked } = resBlock.data || {};

      if (blocked) {
        logFailed("blocked", { otherUid: id });
        showToast(
          t("call_blocked", {
            defaultValue: "🚫 لا يمكن بدء مكالمة لأن أحد الطرفين قام بالحظر",
          }),
          "error"
        );
        return;
      }

      const startCall = httpsCallable(functions, "startCall");
      const res = await startCall({ femaleUid: id });
      const data = res.data || {};

      if (data.reason === "insufficient_balance") {
        logFailed("insufficient_balance", { femaleUid: id });
        showToast(
          t("call_balance_error", { defaultValue: "رصيدك غير كاف 💔" }),
          "error"
        );
        return;
      }

      if (!data.ok || !data.callId) {
        logFailed("no_call_id", { femaleUid: id, data });
        showToast(
          t("call_failed", { defaultValue: "تعذر بدء المكالمة" }),
          "error"
        );
        return;
      }

      startCallStartupTraceAt(data.callId, callButtonTapAt);
      logCallStartupEvent(data.callId, "call_button_tapped", "caller");
      logCallStartupEvent(data.callId, "call_created", "caller");

      logReturned(data.callId, { femaleUid: id });
      const returnContext = buildReturnContextFromLocation(location);
      const uid = auth.currentUser?.uid;
      if (uid && returnContext) {
        saveReturnContext(uid, data.callId, returnContext);
        console.log(
          `[callReturn] captured role=caller callId=${data.callId} url=${returnContext.url}`
        );
      }
      navigate(`/call-outgoing/${data.callId}`, {
        state: {
          returnTo: returnContext?.url || `/profile/${id}`,
          returnContext: returnContext || undefined,
        },
      });
    } catch (err) {
      logFailed(err, { femaleUid: id });
      console.error("startCall error:", err);
      const toast = getStartCallErrorToast(t, err, displayName);
      showToast(toast.message, toast.type);
    }
  };

  // =========================================================
  // تحميل حالة المتابعة — realtime read only
  // =========================================================
  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser || !id) return;
    if (currentUser.uid === id) return;

    const followsRef = collection(db, "follows");
    const q = query(
      followsRef,
      where("fromUid", "==", currentUser.uid),
      where("toUid", "==", id)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        setIsFollowing(!snap.empty);
      },
      (err) => {
        console.error("Follow state error:", err);
      }
    );

    return () => unsub();
  }, [id]);

  // =========================================================
  // زر متابعة / إلغاء متابعة — Cloud Functions only
  // =========================================================
  const toggleFollow = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser || !id) {
      showToast(
        t("login_required", { defaultValue: "يرجى تسجيل الدخول أولاً" }),
        "error"
      );
      return;
    }

    if (currentUser.uid === id) return;
    if (followBusy) return;

    try {
      setFollowBusy(true);

      if (isFollowing) {
        const unfollowUser = httpsCallable(functions, "unfollowUser");
        await unfollowUser({ targetUid: id });

        showToast(
          t("unfollowed_success", { defaultValue: "تم إلغاء المتابعة" }),
          "success"
        );
      } else {
        const followUser = httpsCallable(functions, "followUser");
        await followUser({ targetUid: id });

        showToast(
          t("followed_success", { defaultValue: "تمت المتابعة بنجاح" }),
          "success"
        );
      }
    } catch (err) {
      console.error("toggleFollow error:", err);
      showToast(
        t("follow_action_failed", {
          defaultValue: "تعذر تحديث حالة المتابعة",
        }),
        "error"
      );
    } finally {
      setFollowBusy(false);
    }
  };

  // =========================================================
  // جلب بيانات المستخدم
  // =========================================================
  useEffect(() => {
    let alive = true;

    const fetchUser = async () => {
      try {
        setLoading(true);

        if (!id) {
          if (alive) setUser(null);
          return;
        }

        const currentUser = auth.currentUser;
        const isOwnProfile = Boolean(
          currentUser?.uid && currentUser.uid === id
        );
        const profileCollection = isOwnProfile ? "users" : "publicProfiles";
        const ref = doc(db, profileCollection, id);
        const snap = await getDoc(ref);

        if (!alive) return;

        if (snap.exists()) {
          setUser({ id: snap.id, ...snap.data() });
        } else {
          setUser(null);
        }
      } catch (err) {
        console.error("fetchUser error:", err);
        if (alive) setUser(null);
      } finally {
        if (alive) setLoading(false);
      }
    };

    fetchUser();

    return () => {
      alive = false;
    };
  }, [id]);

  // =========================================================
  // تسجيل زيارة عبر Cloud Function registerVisit
  // — StrictMode-safe: dedupe by profile id without resetting guard in cleanup
  // — visitorAvatar: users/{me}.profileImage | photoURL | Auth photoURL
  // =========================================================
  const visitSentForRef = useRef(null);

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser || !id) return;
    if (currentUser.uid === id) return;

    if (visitSentForRef.current === id) return;
    visitSentForRef.current = id;

    const run = async () => {
      let visitorAvatar = "";

      try {
        const meSnap = await getDoc(doc(db, "users", currentUser.uid));
        if (meSnap.exists()) {
          const me = meSnap.data() || {};
          const pi =
            typeof me.profileImage === "string" ? me.profileImage.trim() : "";
          const pu =
            typeof me.photoURL === "string" ? me.photoURL.trim() : "";
          visitorAvatar = pi || pu || "";
        }
      } catch (e) {
        console.warn("registerVisit me profile read:", e);
      }

      if (!visitorAvatar) {
        const authUrl =
          typeof currentUser.photoURL === "string"
            ? currentUser.photoURL.trim()
            : "";
        visitorAvatar = authUrl || "";
      }

      const registerVisit = httpsCallable(functions, "registerVisit");

      registerVisit({
        visitedUserId: id,
        visitorName:
          currentUser.displayName ||
          t("chat.user", { defaultValue: "مستخدم" }),
        visitorAvatar,
      }).catch((err) => console.error("registerVisit error:", err));
    };

    run();
    // No cleanup: resetting a guard here would allow StrictMode double-fetch;
    // visitSentForRef stays on `id` until user opens another profile (id change).
  }, [id, t]);

  // =========================================================
  // تحميل آخر الهدايا المستلمة
  // =========================================================
  useEffect(() => {
    if (!id) {
      setReceivedGifts([]);
      return;
    }

    const currentUser = auth.currentUser;
    if (!currentUser?.uid) {
      setReceivedGifts([]);
      return;
    }

    if (!profileShowGifts(user)) {
      setReceivedGifts([]);
      return;
    }

    const giftsRef = collection(db, "giftsLog");
    const q = query(
      giftsRef,
      where("to", "==", id),
      orderBy("createdAt", "desc"),
      limit(50)
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));

        setReceivedGifts(rows);
      },
      (err) => {
        console.error("received gifts error:", err);
        setReceivedGifts([]);
      }
    );

    return () => unsub();
  }, [id, user?.showGifts, user?.settings?.messages?.showGifts]);

  // =========================================================
  // الصور (FINAL + UX: preload + skeleton + fade)
  // =========================================================
  const [images, setImages] = useState([]);
  const [loadedMap, setLoadedMap] = useState({});
  const [activeReady, setActiveReady] = useState(false);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      if (!user) {
        if (alive) setImages([]);
        return;
      }

      const rawArr = Array.isArray(user?.photos) ? user.photos : [];
      const items = rawArr
        .map((p) => (typeof p === "string" ? p : p?.path || p?.url || ""))
        .filter(Boolean);

      const fallbackOne = getProfilePhotoRawFromUser(user);

      const finalRaw = items.length ? items : fallbackOne ? [fallbackOne] : [];

      const urls = await Promise.all(finalRaw.map(resolveProfilePhotoUrlCore));

      const clean = urls.filter(
        (u) =>
          typeof u === "string" &&
          (u.startsWith("http") ||
            u.startsWith("blob:") ||
            u.startsWith("data:"))
      );

      if (alive) {
        setImages(clean.length ? clean : []);
        setLoadedMap({});
        setActiveReady(false);
      }
    };

    run();
    return () => {
      alive = false;
    };
  }, [user]);

  // =========================================================
  // السلايدر
  // =========================================================
  const [currentIndex, setCurrentIndex] = useState(0);
  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);

  useEffect(() => {
    setCurrentIndex(0);
  }, [id, images.length]);

  useEffect(() => {
    if (images.length <= 1) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 3500);

    return () => clearInterval(timer);
  }, [images.length]);

  const goToNext = useCallback(() => {
    if (!images.length) return;
    setCurrentIndex((prev) => (prev + 1) % images.length);
  }, [images.length]);

  const goToPrev = useCallback(() => {
    if (!images.length) return;
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e) => {
    if (touchStartX.current === null) return;
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  };

  const handleTouchEnd = () => {
    if (touchDeltaX.current > 50) goToPrev();
    else if (touchDeltaX.current < -50) goToNext();

    touchStartX.current = null;
    touchDeltaX.current = 0;
  };

  // preload current image
  useEffect(() => {
    const url = images?.[currentIndex];
    if (!url) {
      setActiveReady(true);
      return;
    }

    if (loadedMap[url]) {
      setActiveReady(true);
      return;
    }

    setActiveReady(false);

    const im = new Image();
    im.onload = () => {
      setLoadedMap((m) => ({ ...m, [url]: true }));
      setActiveReady(true);
    };
    im.onerror = () => {
      setActiveReady(true);
    };
    im.src = url;
  }, [images, currentIndex, loadedMap]);

  // =========================================================
  // التحميل
  // =========================================================
  if (loading) {
    return (
      <div className="visitor-loading">
        <div className="visitor-spinner"></div>
        <p>
          {t("loading_profile", {
            defaultValue: "جاري تحميل بروفايل المستخدم...",
          })}
        </p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="visitor-loading">
        <p>{t("user_not_found", { defaultValue: "المستخدم غير موجود." })}</p>
      </div>
    );
  }

  // =========================================================
  // ✅ LEVEL
  // =========================================================
  const gender = (user?.gender || "").toLowerCase();
  const shownLevel =
    (gender === "male" ? user?.maleLevel : user?.level) ??
    user?.femaleLevel ??
    0;

  const isFemale = isFemaleUser(user);
  const priceLvl = isFemale ? getFemalePriceLevel(user) : null;
  const pricePerMin = isFemale ? getFemalePricePerMinute(user) : null;

  const hasRealImage = images.length > 0;
  const canShowGifts = profileShowGifts(user);

  const receiveMode = profileReceiveMode(user);
  // Phase N9B: inCall is the server-authoritative Busy mirror (see
  // functions/calls/locks.js) - a Busy user stays fully visible here, just
  // not callable while busy.
  const isBusy = isUserBusy(user);
  const canCall = canCallUserPolicy(user);
  const canMessage = receiveMode !== "off";

  const displayName =
    user?.name ||
    user?.displayName ||
    t("user", { defaultValue: "مستخدم" });

  return (
    <div className="visitor-page livora-a">
      {toast?.message ? (
        <div
          className={`online-toast ${toast.type || "info"}`}
          role="status"
          aria-live="polite"
        >
          {toast.message}
        </div>
      ) : null}

      <div className="la-shell">
        {/* HERO */}
        <div
          className="la-hero-wrap"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="la-card">
            {/* Slider track */}
            <div
              className="la-track"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {images.length === 0 ? (
                <div className="la-slide">
                  <img
                    className="la-img la-gender-default-avatar"
                    src={defaultAvatarByGender(user?.gender)}
                    alt={t("common.avatar", { defaultValue: "Avatar" })}
                    onError={(e) => {
                      e.currentTarget.src = defaultAvatarByGender(
                        user?.gender
                      );
                    }}
                  />
                </div>
              ) : (
                images.map((img, index) => (
                  <div className="la-slide" key={index}>
                    <img
                      src={img}
                      alt={t("profile.image_alt", { defaultValue: "profile" })}
                      className={
                        activeReady && images[currentIndex] === img
                          ? "la-img ready"
                          : "la-img"
                      }
                      loading="eager"
                      onLoad={() =>
                        setLoadedMap((m) => ({ ...m, [img]: true }))
                      }
                      onError={() => {
                        setImages((arr) => arr.filter((x) => x !== img));
                      }}
                    />
                  </div>
                ))
              )}
            </div>

            {/* Skeleton overlay */}
            {hasRealImage && !activeReady && (
              <div className="la-skeleton" aria-hidden="true" />
            )}

            {/* Top buttons */}
            <button
              className="la-topbtn la-back la-glass-btn"
              onClick={() => navigate(-1)}
              type="button"
              aria-label={t("common.back", { defaultValue: "back" })}
            >
              ←
            </button>

            {/* overlay */}
            <div className="la-grad" />

            {/* identity */}
            <div className="la-identity-card">
              <div className="la-follow-corner">
                <button
                  className={
                    isFollowing
                      ? "la-follow active la-glass-btn"
                      : "la-follow la-glass-btn"
                  }
                  onClick={toggleFollow}
                  type="button"
                  disabled={followBusy}
                  title={
                    isFollowing
                      ? t("unfollow", { defaultValue: "إلغاء المتابعة" })
                      : t("follow", { defaultValue: "متابعة" })
                  }
                >
                  {followBusy
                    ? t("loading", { defaultValue: "..." })
                    : isFollowing
                    ? t("following", { defaultValue: "Following" })
                    : t("follow", { defaultValue: "Follow" })}
                </button>
              </div>

              <div className="la-name-block">
                <div className="la-name">{displayName}</div>

                <div className="la-online-row">
                  <div
                    className={`la-meta-pill la-online-pill la-glass-chip ${
                      isBusy
                        ? "is-busy"
                        : user?.online
                        ? "is-online"
                        : "is-offline"
                    }`}
                  >
                    <span
                      className={
                        isBusy ? "la-dot busy" : user?.online ? "la-dot on" : "la-dot off"
                      }
                    />
                    <span>
                      {isBusy
                        ? t("presence.busy_now", { defaultValue: "Busy now" })
                        : user?.online
                        ? t("online_now", { defaultValue: "Online" })
                        : t("offline_now", { defaultValue: "Offline" })}
                    </span>
                  </div>
                </div>

                <div className="la-country-row">
                  <div
                    className="la-meta-pill la-country-pill la-glass-chip"
                    title={countryUI.text}
                  >
                    <CountryFlagGlyph
                      countryCode={countryUI.code}
                      className="la-flag"
                      imgClassName="la-flag country-flag-glyph__img"
                    />
                    <span className="la-country-text">{countryUI.text}</span>
                  </div>
                </div>

                <div className="la-bottom-badges-row">
                  <div className="la-combo-pill la-glass-chip">
                    <div
                      className="la-combo-segment la-combo-level"
                      title={t("level", { defaultValue: "المستوى" })}
                    >
                      <span className="la-level-pill-label">Lv</span>
                      <span className="la-level-pill-value">{shownLevel}</span>
                    </div>

                    {userAge ? (
                      <div
                        className="la-combo-segment la-combo-age"
                        title={t("age", { defaultValue: "العمر" })}
                      >
                        <FaBirthdayCake className="la-pill-ico" />
                        {/* Explicit Western-digit formatting, not implicit
                            Number-to-string - Arabic UI must never show
                            Arabic-Indic digits for this value. */}
                        <span dir="ltr">{formatNumberEn(userAge)}</span>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            {/* dots */}
            {images.length > 1 && (
              <div className="la-dots">
                {images.map((_, idx) => (
                  <button
                    key={idx}
                    className={
                      idx === currentIndex
                        ? "la-dotbtn active la-glass-dot"
                        : "la-dotbtn la-glass-dot"
                    }
                    onClick={() => setCurrentIndex(idx)}
                    type="button"
                    aria-label={t("profile.slider_dot", {
                      defaultValue: "dot-{{index}}",
                      index: idx,
                    })}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ACTIONS */}
        <div className="la-actions">
          <button
            className={`la-call la-glass-action${
              canCall ? "" : " la-call--receive-off"
            }`}
            onClick={() => {
              if (starting) return;
              const tap = resolveCallTapAction(user, t);
              if (tap.action === "busy") {
                showToast(tap.message, "error");
                return;
              }
              if (tap.action !== "call") return;
              handleStartCall();
            }}
            type="button"
            disabled={(receiveMode !== "both" && !isBusy) || starting}
            aria-disabled={!canCall || starting}
            aria-busy={starting}
            title={
              !canCall
                ? isBusy || receiveMode === "off"
                  ? t("calls.user_unavailable", {
                      defaultValue: "User unavailable",
                    })
                  : t("calls.not_available", {
                      defaultValue: "المكالمات غير متاحة",
                    })
                : isFemale && pricePerMin != null
                ? t("calls.level_price_per_min", {
                    defaultValue: "Lv{{level}} • {{price}} / min",
                    level: priceLvl,
                    price: pricePerMin,
                  })
                : t("call", { defaultValue: "Call" })
            }
          >
            <div className="la-ico-wrap la-glass-icon">
              <FaVideo className="la-ico" />
            </div>

            <div className="la-action-text">
              <div className="la-action-title">
                {canCall
                  ? t("call_now", { defaultValue: "Call Now" })
                  : isBusy
                  ? t("presence.busy_now", { defaultValue: "Busy now" })
                  : receiveMode === "off"
                  ? t("calls.user_unavailable", {
                      defaultValue: "User unavailable",
                    })
                  : t("calls.not_available", {
                      defaultValue: "المكالمات غير متاحة",
                    })}
              </div>

              <div className="la-action-sub">
                {canCall ? (
                  isFemale && pricePerMin != null ? (
                    <>
                      <img
                        src={goldCoin}
                        alt={t("wallet.coin_alt", { defaultValue: "coin" })}
                        className="la-coin"
                      />
                      <span>{pricePerMin}</span>
                      <span className="la-muted">
                        / {t("minute", { defaultValue: "min" })}
                      </span>
                    </>
                  ) : (
                    <span className="la-muted">
                      — / {t("minute", { defaultValue: "min" })}
                    </span>
                  )
                ) : (
                  <span className="la-muted">
                    {t("common.unknown", { defaultValue: "—" })}
                  </span>
                )}
              </div>
            </div>
          </button>

          <button
            className={`la-msg la-glass-action${
              canMessage ? "" : " la-msg--receive-off"
            }`}
            onClick={handleMessageClick}
            type="button"
            disabled={!canMessage}
            title={
              !canMessage
                ? t("calls.user_unavailable", {
                    defaultValue: "User unavailable",
                  })
                : undefined
            }
          >
            <div className="la-ico-wrap la-glass-icon">
              <FaRegEnvelope className="la-ico" />
            </div>

            <div className="la-action-text">
              <div className="la-action-title">
                {canMessage
                  ? t("message", { defaultValue: "Message" })
                  : t("calls.user_unavailable", {
                      defaultValue: "User unavailable",
                    })}
              </div>
              <div className="la-action-sub la-muted">
                {canMessage
                  ? t("start_chat", { defaultValue: "ابدأ المحادثة" })
                  : t("common.unknown", { defaultValue: "—" })}
              </div>
            </div>
          </button>
        </div>

        {/* GIFTS */}
        {canShowGifts && (
          <div className="la-gifts">
            <div className="la-gifts-head">
              <div className="la-gifts-left">
                <span className="la-gift-icon">🎁</span>
                <span className="la-gifts-title">
                  {t("received_gifts", { defaultValue: "Received Gifts" })}
                </span>
                <span className="la-gifts-dash" />
              </div>

              <div className="la-sendgift la-glass-btn" aria-hidden="true">
                {totalReceivedGiftCount} {t("gift", { defaultValue: "Gifts" })}
              </div>
            </div>

            {groupedReceivedGifts.length > 0 ? (
              <div className="la-gifts-grid">
                {groupedReceivedGifts.map((gift) => (
                  <button
                    key={gift.key}
                    className="la-gift-card la-glass-card"
                    type="button"
                  >
                    <div className="la-gift-counter">x{gift.count}</div>

                    <div className="la-gift-img">
                      <ReceivedGiftVisual
                        giftPreview={gift.giftPreview}
                        giftIcon={gift.giftIcon}
                        giftName={gift.giftName}
                      />
                    </div>

                    <div className="la-gift-name">{gift.giftName}</div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="la-glass-card" style={{ padding: 16 }}>
                <div className="la-action-title" style={{ marginBottom: 6 }}>
                  {t("no_received_gifts", {
                    defaultValue: "No gifts received yet",
                  })}
                </div>
                <div className="la-action-sub la-muted">
                  {t("received_gifts_hint", {
                    defaultValue: "الهدايا التي يستلمها هذا المستخدم ستظهر هنا",
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="la-bottom-space" />
      </div>
      {overlay}
    </div>
  );
}

export default UserProfilePage;